import React, { useEffect, useMemo, useState, useCallback } from "react";

import AchievementRate from "../../../organisms/student/ActionList/AchievementRate";
import TabBar from "../../../organisms/student/ActionList/TabBar";
import VolumeList from "../../../organisms/student/ActionList/VolumeList";
import ActionCard from "../../../organisms/student/ActionList/ActionCard";
import ActionModal from "../../../organisms/student/ActionList/ActionModal";
import { IActionList, IActionLessons } from "../../../state";

import styles from "./ActionList.module.scss";
export interface IActionListProps {
  actionList: IActionList;
  handleSubmitRealization: (
    lesson_id: number,
    content: string,
    realization_id: number | null,
  ) => void;
  calling: boolean;
  refetchData: () => void;
}
const ActionList: React.FC<IActionListProps> = ({
  actionList,
  handleSubmitRealization,
  calling,
  refetchData,
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [activeVolume, setActiveVolume] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>("高校");
  const [modalLesson, setModalLesson] = useState<IActionLessons | null>(null);

  const { achieved, total } = useMemo(() => {
    const totalAnsweredLessons = actionList.teaching_materials.reduce(
      (count, material) => {
        const lessonsWithAnswerCount =
          material.lessons?.filter(
            lesson =>
              lesson.answer?.contents?.[1]?.content !== undefined &&
              lesson.answer?.contents?.[1]?.content !== "",
          ).length || 0;
        return count + lessonsWithAnswerCount;
      },
      0,
    );

    const achievedLessonsCount = actionList.teaching_materials.reduce(
      (total, material) =>
        total +
        material.lessons!.filter(
          lesson => lesson.reflection_realization_reference !== null,
        ).length,
      0,
    );

    return { achieved: achievedLessonsCount, total: totalAnsweredLessons };
  }, [actionList]);

  const { middleSchoolCount, highSchoolCount } = useMemo(() => {
    let middleSchoolCount = 0;
    let highSchoolCount = 0;
    actionList.teaching_materials.forEach(vol => {
      if (vol.title.includes("高校")) {
        highSchoolCount++;
      }
      if (vol.title.includes("中学")) {
        middleSchoolCount++;
      }
    });
    return {
      highSchoolCount,
      middleSchoolCount,
    };
  }, [actionList]);

  const filteredVolumesBySchoolType = useMemo(() => {
    const filtered = actionList.teaching_materials.filter(vol => {
      if (selectedTab === "中学") {
        return vol.title.includes("中学");
      } else if (selectedTab === "高校") {
        return vol.title.includes("高校");
      }
      return false;
    });
    return filtered;
  }, [actionList, selectedTab]);

  const checkAnswerContent = useCallback(volume => {
    if (!volume.lessons || volume.lessons.length === 0) {
      return false;
    }
    return volume.lessons.some((lesson: IActionLessons) => {
      return lesson.answer?.contents?.[1] !== undefined;
    });
  }, []);

  // 両方の条件を満たす場合にのみ有効（disabled状態でない）と判定する
  const hasAnsweredLessons = useCallback(
    volume =>
      volume.lessons && volume.lessons.length > 0 && checkAnswerContent(volume),
    [checkAnswerContent],
  );

  const areAllVolumesUnanswered = useMemo(() => {
    return filteredVolumesBySchoolType.every(
      volume => !hasAnsweredLessons(volume),
    );
  }, [filteredVolumesBySchoolType, hasAnsweredLessons]);

  useEffect(() => {
    setModalOpen(false);
  }, [activeVolume]);

  const activeVolumeData = useMemo(() => {
    const filtered = actionList.teaching_materials.filter(vol => {
      if (selectedTab === "中学") {
        return vol.title.includes("中学");
      } else if (selectedTab === "高校") {
        return vol.title.includes("高校");
      }
      return false;
    });

    return filtered.find(vol => vol.name === activeVolume);
  }, [actionList, selectedTab, activeVolume]);

  useEffect(() => {
    if (filteredVolumesBySchoolType.length > 0) {
      const firstVolumeWithAnsweredLessons = filteredVolumesBySchoolType.find(
        volume => hasAnsweredLessons(volume),
      );

      if (firstVolumeWithAnsweredLessons && activeVolume === null) {
        setActiveVolume(firstVolumeWithAnsweredLessons.name);
      }
    }
  }, [filteredVolumesBySchoolType, activeVolume, hasAnsweredLessons]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.volumeMenu}>
        <AchievementRate achieved={achieved} total={total} />
        <div className={styles.tabBarWrapper}>
          <TabBar
            highSchoolCount={highSchoolCount}
            middleSchoolCount={middleSchoolCount}
            actionList={actionList}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            setActiveVolume={setActiveVolume}
          />
        </div>
        <div className={styles.volumeListWrapper}>
          <VolumeList
            activeVolume={activeVolume}
            setActiveVolume={setActiveVolume}
            volumeList={filteredVolumesBySchoolType}
            hasAnsweredLessons={hasAnsweredLessons}
          />
        </div>
      </div>
      {areAllVolumesUnanswered ? (
        <div className={styles.rightContainer}>
          <p className={styles.message}>
            この画面には、あなたが授業中に入力した行動宣言が表示されます。
            <br />
            授業で学んだ考え方を、日常生活で使ってみましょう。
          </p>
        </div>
      ) : modalOpen ? (
        <div className={styles.rightContainer}>
          <ActionModal
            setModalOpen={setModalOpen}
            modalOpen={modalOpen}
            modalLesson={modalLesson}
            handleSubmitRealization={handleSubmitRealization}
            calling={calling}
            refetch={refetchData}
          />
        </div>
      ) : (
        <div className={styles.rightContainer}>
          {activeVolumeData?.lessons?.map(lesson => {
            if (lesson.answer === null) return null;
            return (
              <div className={styles.actionCardWrapper} key={lesson.id}>
                <ActionCard
                  lesson={lesson}
                  setModalLesson={setModalLesson}
                  modalOpen={modalOpen}
                  setModalOpen={setModalOpen}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActionList;
